/**
 * catastroEnricher.ts — Helper puro para tooltip de datos catastrales
 *
 * Convierte un CatastroR2Parcel (Worker R2, ~30 ms) en los datos que
 * muestran P2 y la exportación (superficie construida de la parcela,
 * plantas, uso, año y accesibilidad), sin peticiones al WFS INSPIRE.
 * Desde `ALBCAT-a` no estima aforo.
 *
 * Nota: el campo `antiguedad` lo puebla CAT-RICH-1 con cobertura parcial
 * (≈18-82 % según lote); es null cuando el dato catastral de origen no lo expone.
 *
 * @module services/catastro/catastroEnricher
 * @version 1.0.0
 */

import type { CatastroR2Parcel } from '../../types/catastroR2';

// =============================================================================
// INTERFAZ PÚBLICA
// =============================================================================

export interface CatastroTooltipData {
  /**
   * Referencia catastral de la parcela. `REFCAT` · DOP-1=(a): la completa de
   * 14 caracteres (`refcat_canonico`); solo si el dato no la trae, la corta
   * heredada (`refcat`: código DGC + 7 caracteres), que en rústica comparten
   * todas las parcelas del municipio.
   */
  rc?: string;
  /**
   * Superficie construida de la parcela en m² (`sup_construida`; 0 si no viene).
   * `ALBCAT-a` · DOP-5=(a): es de la parcela, no del edificio del elemento, y ya no
   * cae a `sup_total` sin decirlo (medido: 0 parcelas con construida 0 y total > 0
   * en Guadix, Carboneras, Mancha Real y Colomera).
   */
  superficie: number;
  /** Número de plantas sobre rasante */
  plantas: number;
  /** Uso para mostrar: el `uso` que trae el dato; la tabla, solo de reserva */
  uso: string;
  /** Año de construcción (null si no disponible) */
  anoConstr?: number;
  // `ALBCAT-a` · DOP-4=(b): `aforoEstimado`, `aforoMetodo` y `aforoConservador`
  // retirados junto con `calcAforo` (sin lector desde `AFORET`; nunca una cifra de
  // personas desde Catastro). Las columnas `CAT_AFORO_*` siguen vacías.
  /** Accesibilidad inferida: probable | verificar | desconocida */
  accesibilidad: 'probable' | 'verificar' | 'desconocida';
  /** Etiqueta legible de accesibilidad */
  accesibilidadLabel: string;
}

// =============================================================================
// TABLAS INTERNAS
// =============================================================================

/**
 * Etiquetas de reserva por código de uso de la DGC, para cuando el dato no trae la
 * suya. `ALBCAT-a` · DOP-4b=(a): derivadas del dato, no de memoria — los pares
 * `(uso_cod, uso)` del volcado R2 (Colomera en `FUS1`; Guadix, Carboneras y Mancha
 * Real en `ALBCAT`), los mismos que documenta `types/catastroR2.ts`. La tabla anterior
 * pisaba la etiqueta del dato y la contradecía (K «Deportivo» salía «Oficinas
 * (admin.)», M «Suelo sin edificar» salía «Ocio y hostelería», P «Público» salía
 * «Religioso», G «Turismo» salía «Servicios públicos»). `O` se conserva como
 * «Oficinas» (así lo usa `tipologiaACodigoCatastro`); `J` y `Z` no traen etiqueta
 * en el dato y no se les atribuye ninguna.
 */
const USO_COD_LABEL: Record<string, string> = {
  E: 'Educativo',
  Y: 'Sanitario',
  P: 'Público',
  R: 'Religioso',
  K: 'Deportivo',
  T: 'Espectáculos',
  G: 'Turismo',
  I: 'Industrial',
  C: 'Comercial',
  V: 'Vivienda',
  A: 'Almacén',
  M: 'Suelo sin edificar',
  O: 'Oficinas',
};

// =============================================================================
// HELPERS INTERNOS (reutilizan lógica de catastroService.ts)
// =============================================================================

/**
 * Etiqueta de uso.
 * Prioridad (`ALBCAT-a` · DOP-4b=(a)): `parcel.uso` si es una etiqueta (no vacía y
 * distinta del propio código) → `USO_COD_LABEL[uso_cod]` → el código tal cual
 * (`J`, `Z`: sin etiqueta en el dato, no se inventa) → 'Desconocido'.
 */
function resolveUso(parcel: CatastroR2Parcel): string {
  const cod = typeof parcel.uso_cod === 'string' ? parcel.uso_cod.trim() : '';
  const uso = typeof parcel.uso === 'string' ? parcel.uso.trim() : '';
  if (uso && uso !== cod) return uso;
  return USO_COD_LABEL[cod] ?? (cod || 'Desconocido');
}

/**
 * Superficie construida de la parcela (`sup_construida`), o 0 si no viene.
 * `ALBCAT-a` · DOP-5=(a): sin caer a `sup_total` sin decirlo.
 */
function resolveSuperficie(parcel: CatastroR2Parcel): number {
  return typeof parcel.sup_construida === 'number' && parcel.sup_construida > 0
    ? parcel.sup_construida
    : 0;
}

/**
 * Infiere accesibilidad por normativa vigente + número de plantas.
 * Replica exactamente inferAccesibilidad() de catastroService.ts.
 */
function inferAccesibilidad(
  anoConstr: number | undefined,
  plantas: number
): Pick<CatastroTooltipData, 'accesibilidad' | 'accesibilidadLabel'> {
  if (!anoConstr) {
    return { accesibilidad: 'desconocida', accesibilidadLabel: 'Dato no disponible' };
  }
  if (plantas === 1) {
    return { accesibilidad: 'probable', accesibilidadLabel: 'Planta baja (probable)' };
  }
  if (anoConstr >= 2010) {
    return { accesibilidad: 'probable', accesibilidadLabel: `Posterior CTE 2010 (probable)` };
  }
  if (anoConstr >= 1994) {
    return { accesibilidad: 'verificar', accesibilidadLabel: `LMRPD 1994–2010 (verificar)` };
  }
  return { accesibilidad: 'verificar', accesibilidadLabel: `Anterior normativa (verificar)` };
}

// =============================================================================
// FUNCIÓN PÚBLICA
// =============================================================================

/**
 * Enriquece un CatastroR2Parcel con los datos necesarios para el tooltip.
 *
 * Sin aforo: Catastro no da cifras de personas (`ALBCAT-a` · DOP-4=(b)).
 *
 * Lógica de superficie: `sup_construida` de la parcela, o 0 (sin `sup_total`).
 *
 * Lógica de accesibilidad (proxy normativo, NO oficial):
 *   - sin año          → desconocida
 *   - plantas === 1    → probable («Planta baja»)
 *   - año >= 2010      → probable (CTE 2010)
 *   - año >= 1994      → verificar (LMRPD)
 *   - año < 1994       → verificar («Anterior normativa»)
 *
 * @param parcel - Parcela servida por Worker R2
 * @returns Datos listos para renderizar en tooltip Leaflet
 */
export function enrichCatastroTooltip(parcel: CatastroR2Parcel): CatastroTooltipData {
  const superficie = resolveSuperficie(parcel);
  const plantas = parcel.plantas ?? 0;
  const uso = resolveUso(parcel);

  const { accesibilidad, accesibilidadLabel } = inferAccesibilidad(
    parcel.antiguedad,
    plantas
  );

  return {
    rc: parcel.refcat_canonico || parcel.refcat,
    superficie,
    plantas,
    uso,
    anoConstr: parcel.antiguedad ?? undefined,
    accesibilidad,
    accesibilidadLabel,
  };
}
